import { useState } from "react";
import { IClinicModalProps } from "../types/modal";

import { useAppDispatch } from "../hooks/redux";
import { closeModalById, openModal } from "../features/modals";

import { useFormik } from "formik";
import axios from "axios";

import {
  Dialog,
  DialogActions,
  DialogTitle,
  Button,
  DialogContent,
  TextField,
  Stack,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import clinicSchema, { ClinicBase } from "../schemas/clinic";
import MODALS from "../constants/modals";
import PermissionGuard from "../components/PermissionGuard";
import PERMISSIONS from "../constants/permissions";

const ClinicModal: React.FunctionComponent<IClinicModalProps> = ({
  id,
  callback,
  initialValues,
}) => {
  const dispatch = useAppDispatch();

  const [isDeleting, setIsDeleting] = useState(false);

  const {
    values,
    dirty,
    handleChange,
    setFieldValue,
    handleSubmit,
    errors,
    resetForm,
    submitCount,
  } = useFormik<ClinicBase>({
    initialValues: {
      name: initialValues ? initialValues.name : "",
      publicName: initialValues?.publicName,
      phone: initialValues?.phone,
      address: initialValues?.address,
      order: initialValues?.order,
    },
    onSubmit: (values) => {
      axios
        .put(
          "/clinics",
          initialValues ? { _id: initialValues._id, ...values } : values
        )
        .then(() => {
          callback();
          handleClose();
        })
        .catch((err) => {
          console.log("err:", err);
        });
    },
    validationSchema: clinicSchema,
  });

  const handleDelete = (id: string) => {
    setIsDeleting(true);
    axios
      .delete(`/clinics/${id}`)
      .then(() => {
        callback();
        handleClose();
      })
      .catch((err) => {
        console.log("err:", err);
        setIsDeleting(false);
      });
  };

  const handleClose = () => {
    dispatch(closeModalById(id));
  };

  return (
    <Dialog fullWidth={true} maxWidth="xs" open={true} onClose={handleClose}>
      <DialogTitle>
        {"_id" in values ? "Labot" : "Pievienot"} klīniku
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <TextField
            id="name"
            label="Nosaukums"
            value={values.name}
            onChange={handleChange}
            error={Boolean(errors.name) && submitCount > 0}
          />
          <TextField
            id="publicName"
            label="Publiskais nosaukums (neobligāts)"
            defaultValue={values.publicName || ""}
            onChange={handleChange}
            error={Boolean(errors.name) && submitCount > 0}
          />
          <TextField
            id="address"
            label="Adrese (neobligāts)"
            defaultValue={values.address}
            onChange={handleChange}
            error={Boolean(errors.name) && submitCount > 0}
          />
          <TextField
            id="phone"
            label="Telefons (neobligāts)"
            defaultValue={values.phone}
            onChange={handleChange}
            error={Boolean(errors.name) && submitCount > 0}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <PermissionGuard permission={PERMISSIONS.DELETE_CLINICS}>
          {({ hasAccess }) => (
            <>
              {hasAccess && initialValues ? (
                <LoadingButton
                  loading={isDeleting}
                  onClick={() => {
                    dispatch(
                      openModal({
                        type: MODALS.Approve,
                        title: "Vai tiešām dzēst klīniku?",
                        message:
                          "Klīnika un visi tās saistītie dati tiks dzēsti!",
                        onApprove: () => {
                          handleDelete(initialValues._id);
                        },
                      })
                    );
                  }}
                  color="error"
                  variant="outlined"
                >
                  Dzēst
                </LoadingButton>
              ) : null}
            </>
          )}
        </PermissionGuard>
        <Stack
          direction="row"
          spacing={2}
          flexGrow={1}
          justifyContent="flex-end"
        >
          <Button
            disabled={isDeleting}
            onClick={() => {
              handleSubmit();
            }}
            variant="contained"
          >
            {initialValues ? "Saglabāt" : "Pievienot"}
          </Button>
          <Button onClick={handleClose}>Aizvērt</Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default ClinicModal;
